/**
 * Platform → RapidSOS credentials: RapidSOS's own keys for catalog integrations (Google Maps,
 * Twilio, Esri, LiveKit), which organizations without keys of their own use. Each declared value
 * shows where it comes from (studio-core, Studio's env, Parameter Store) or that it is missing,
 * never the value. Values are set and rotated in studio-core, or copied once from an
 * organization's vault that already holds them. A change reaches every API and worker pod
 * within a minute.
 */
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  APIError,
  adminClient,
  type AdminCatalogCredential,
  type AdminCatalogCredentialValue,
} from '@/api/adminClient';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadError } from '@/components/ui/load-state';
import { SecretInput } from '@/components/ui/secret-input';
import { cn } from '@/lib/utils';

/** Where a value comes from, in plain words. */
export function sourceText(value: AdminCatalogCredentialValue): string {
  if (value.source === 'core') return 'Set here';
  if (value.source === 'env') return `Studio env (${value.envName ?? 'env var'})`;
  if (value.source === 'parameter') return 'Parameter Store';
  return 'Missing';
}

/** Values in use that are not in studio-core yet, so nobody here can see or rotate them. */
export function legacyValues(credential: AdminCatalogCredential): AdminCatalogCredentialValue[] {
  return credential.sharedWith ? [] : credential.values.filter((value) => value.source === 'env' || value.source === 'parameter');
}

/** The integrations whose identity is incomplete, so organizations without their own keys cannot use them. */
export function missingIntegrations(credentials: AdminCatalogCredential[]): AdminCatalogCredential[] {
  return credentials.filter((credential) => !credential.ready);
}

function ValueRow({ value }: { value: AdminCatalogCredentialValue }) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-1.5 text-sm">
      <span className="font-mono text-xs">
        {value.name}
        {!value.identity ? <span className="ml-2 font-sans text-muted-foreground">optional</span> : null}
        {value.browserSafe ? <span className="ml-2 font-sans text-muted-foreground">reaches the browser</span> : null}
      </span>
      <span
        className={cn(
          'text-xs',
          value.source === 'core' && 'text-success',
          (value.source === 'env' || value.source === 'parameter') && 'text-warning',
          value.source === null && (value.identity ? 'font-medium text-destructive' : 'text-muted-foreground'),
        )}
        title={value.parameterPath}
      >
        {sourceText(value)}
      </span>
    </li>
  );
}

export function CatalogCredentialCard({
  credential,
  busy,
  onSave,
  onCopy,
}: {
  credential: AdminCatalogCredential;
  busy: boolean;
  onSave: (values: Record<string, string>) => Promise<boolean>;
  onCopy: (organizationId: string) => Promise<boolean>;
}) {
  const [mode, setMode] = useState<'idle' | 'set' | 'copy'>('idle');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [organizationId, setOrganizationId] = useState('');
  const owned = credential.values.filter((value) => value.slot.integrationId === credential.integrationId);
  const legacy = legacyValues(credential);

  const close = () => {
    setMode('idle');
    setDraft({});
    setOrganizationId('');
  };

  return (
    <div className="rounded-xl border border-border bg-card/40 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{credential.title}</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-medium',
              credential.ready ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive',
            )}
          >
            {credential.ready ? 'Ready' : 'Missing'}
          </span>
        </div>
        {credential.sharedWith ? (
          <span className="text-xs text-muted-foreground">Uses {credential.sharedWith.title}&apos;s credentials</span>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === 'set' ? 'idle' : 'set')}>
              Set or rotate
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === 'copy' ? 'idle' : 'copy')}>
              Copy from an organization
            </Button>
          </div>
        )}
      </div>
      <ul className="mt-2 divide-y divide-border/60">
        {credential.values.map((value) => (
          <ValueRow key={value.name} value={value} />
        ))}
      </ul>
      <div className="mt-1 text-[11px] text-muted-foreground">
        {credential.updatedAt ? `Last changed here ${new Date(credential.updatedAt).toLocaleString()}` : 'Never set here'}
        {legacy.length ? ' · set the values from the env or Parameter Store here so they can be rotated from Studio Admin' : ''}
        {credential.allOrNothing && !credential.sharedWith ? ' · the identity values only work together' : ''}
      </div>
      {mode === 'set' ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            const values = Object.fromEntries(Object.entries(draft).filter(([, value]) => value.trim()));
            void onSave(values).then((saved) => saved && close());
          }}
        >
          {owned.map((value) => (
            <label key={value.name} className="block text-xs">
              <span className="mb-1 block font-mono">{value.name}</span>
              <SecretInput
                value={draft[value.name] ?? ''}
                placeholder={value.source ? 'Leave empty to keep the current value' : 'Not set'}
                onChange={(event) => setDraft({ ...draft, [value.name]: event.target.value })}
              />
            </label>
          ))}
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={busy || !Object.values(draft).some((value) => value.trim())}>
              Save
            </Button>
            <Button size="sm" type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {mode === 'copy' ? (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void onCopy(organizationId.trim()).then((copied) => copied && close());
          }}
        >
          <label className="block min-w-[18rem] flex-1 text-xs">
            <span className="mb-1 block">Organization id whose vault already holds these values</span>
            <Input value={organizationId} placeholder="org_…" onChange={(event) => setOrganizationId(event.target.value)} />
          </label>
          <Button size="sm" type="submit" disabled={busy || !organizationId.trim()}>
            Copy
          </Button>
          <Button size="sm" type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
        </form>
      ) : null}
    </div>
  );
}

export function CatalogCredentialsSection() {
  const [credentials, setCredentials] = useState<AdminCatalogCredential[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();

  const load = useCallback(() => {
    setError(undefined);
    adminClient
      .listCatalogCredentials()
      .then((response) => setCredentials(response.integrations))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load RapidSOS credentials'));
  }, []);

  useEffect(load, [load]);

  const save = async (credential: AdminCatalogCredential, values: Record<string, string>): Promise<boolean> => {
    setBusy(credential.integrationId);
    try {
      const response = await adminClient.putCatalogCredential(credential.integrationId, values);
      setCredentials(response.integrations);
      toast.success(`Saved ${credential.title}. Every organization uses it within a minute.`);
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
      return false;
    } finally {
      setBusy(undefined);
    }
  };

  const copy = async (credential: AdminCatalogCredential, organizationId: string, replace = false): Promise<boolean> => {
    setBusy(credential.integrationId);
    try {
      const response = await adminClient.copyCatalogCredentialFromOrganization(credential.integrationId, organizationId, replace);
      setCredentials(response.integrations);
      toast.success(
        response.result.unchanged
          ? `${credential.title} already holds that organization's values`
          : `Copied ${response.result.copied.join(', ')} into RapidSOS's ${credential.title} credentials`,
      );
      return true;
    } catch (err) {
      if (err instanceof APIError && err.status === 409 && !replace && window.confirm(`${err.message}. Overwrite it?`)) {
        setBusy(undefined);
        return copy(credential, organizationId, true);
      }
      toast.error(err instanceof Error ? err.message : 'Failed to copy');
      return false;
    } finally {
      setBusy(undefined);
    }
  };

  const missing = credentials ? missingIntegrations(credentials) : [];

  return (
    <AdminSection
      title="RapidSOS credentials"
      description="RapidSOS's own keys for catalog integrations. Organizations without keys of their own use these. They are kept in studio-core and read ahead of Studio's env and Parameter Store, and a change reaches every organization within a minute. Values are never shown."
    >
      {error ? (
        <LoadError message={error} onRetry={load} />
      ) : !credentials ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading RapidSOS credentials
        </div>
      ) : (
        <div className="space-y-3">
          <p className={cn('text-sm', missing.length ? 'text-destructive' : 'text-muted-foreground')}>
            {missing.length
              ? `Missing for ${missing.map((credential) => credential.title).join(', ')}: organizations without their own keys see these as not available.`
              : 'Every catalog integration has RapidSOS credentials.'}
          </p>
          {credentials.map((credential) => (
            <CatalogCredentialCard
              key={credential.integrationId}
              credential={credential}
              busy={busy === credential.integrationId}
              onSave={(values) => save(credential, values)}
              onCopy={(organizationId) => copy(credential, organizationId)}
            />
          ))}
        </div>
      )}
    </AdminSection>
  );
}
